import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Radio, Navigation, Car, Headset } from "lucide-react";

const GoogleIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} width="18" height="18">
    <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.24 1.4-1.7 4-5.5 4-3.3 0-6-2.75-6-6.1S8.7 5.9 12 5.9c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.9 3.4 14.7 2.5 12 2.5 6.7 2.5 2.5 6.7 2.5 12s4.2 9.5 9.5 9.5c5.5 0 9.1-3.8 9.1-9.2 0-.6-.06-1.1-.15-1.6H12z"/>
    <path fill="#4285F4" d="M21.15 12.3c0-.62-.06-1.2-.15-1.6H12v3.9h5.5c-.24 1.4-1.7 4-5.5 4v-6.3h9.15z"/>
    <path fill="#FBBC05" d="M6 14.2l-.8.6-2.15 1.7C4.7 19.6 8.1 21.5 12 21.5c2.7 0 5-0.9 6.7-2.4l-3.2-2.5c-.9.6-2 1-3.5 1-2.7 0-5-1.8-5.8-4.3z"/>
    <path fill="#34A853" d="M18.7 19.1c1.7-1.5 2.7-3.9 2.7-6.8h-9.4v3.9h5.5c-.3 1.3-1 2.3-2 3l3.2 2.4z"/>
  </svg>
);

export default function Login() {
  const { login, loginWithGoogle } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("dispatcher");
  const [processingGoogle, setProcessingGoogle] = useState(false);
  const processedRef = useRef(false);

  const goByRole = useCallback((role) => {
    if (role === "admin") nav("/admin");
    else if (role === "dispatcher") nav("/dispatch");
    else nav("/driver");
  }, [nav]);

  // Handle #session_id=... after returning from Google OAuth
  useEffect(() => {
    if (processedRef.current) return;
    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) return;
    processedRef.current = true;
    const sid = decodeURIComponent(match[1]);
    // Clear the hash immediately to prevent reprocessing on refresh
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    setProcessingGoogle(true);
    (async () => {
      const res = await loginWithGoogle(sid);
      setProcessingGoogle(false);
      if (!res.ok) {
        toast.error(res.error || "No se pudo iniciar sesión con Google");
        return;
      }
      toast.success(`Bienvenido, ${res.user.name || res.user.email}`);
      goByRole(res.user.role);
    })();
  }, [loginWithGoogle, goByRole]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (!res.ok) {
      toast.error(res.error || "Login fallido");
      return;
    }
    toast.success(`Bienvenido, ${res.user.name || res.user.email}`);
    goByRole(res.user.role);
  };

  const startGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/login";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="relative min-h-screen overflow-hidden isolate">
      {/* Full-screen backdrop image (taxi + Naples city) */}
      <div
        className="absolute inset-0 z-0 bg-cover bg-center"
        style={{ backgroundImage: "url(https://images.unsplash.com/photo-1630717285906-29364ffacea0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwzfHx0YXhpJTIwY2FyJTIwZmxlZXR8ZW58MHx8fHwxNzg1MDk1NDQ0fDA&ixlib=rb-4.1.0&q=85)" }}
        aria-hidden="true"
      />
      {/* Cinematic dark tint so text and card stay legible without hiding the photo */}
      <div className="absolute inset-0 z-0 bg-gradient-to-br from-black/55 via-black/35 to-black/70" aria-hidden="true" />

      <div className="relative z-10 min-h-screen grid lg:grid-cols-2">
        {/* Left: Brand panel */}
        <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden">
          <div className="relative z-10 flex items-center gap-3">
            <div className="relative w-14 h-14 rounded-md bg-white/95 grid place-items-center shadow-[0_0_25px_rgba(251,191,36,0.5)] p-1.5">
              <img
                src="/logo.webp"
                alt="Mi Taxi Naples"
                className="w-full h-full object-contain"
                data-testid="brand-logo-img"
              />
            </div>
            <div>
              <div className="text-white font-black text-xl tracking-tight drop-shadow-lg" style={{ fontFamily: "Manrope" }}>MI TAXI NAPLES DISPATCH</div>
              <div className="text-xs text-white/70 tracking-widest uppercase drop-shadow">Control Room · FL</div>
            </div>
          </div>
          <div className="relative z-10 space-y-6">
            <h1 className="text-5xl xl:text-6xl font-black text-white leading-[1.05] drop-shadow-[0_4px_16px_rgba(0,0,0,0.75)]" style={{ fontFamily: "Manrope" }}>
              Toma el mando<br/>
              de tu flota.
            </h1>
            <p className="text-white/85 max-w-md drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
              Panel de despacho en tiempo real para Naples. Asigna viajes, monitorea conductores y controla precios manualmente.
            </p>
            <div className="flex gap-6 pt-4">
              <div className="flex items-center gap-2 text-sm text-white/80 drop-shadow">
                <Radio className="w-4 h-4 text-primary" /> Live tracking
              </div>
              <div className="flex items-center gap-2 text-sm text-white/80 drop-shadow">
                <Navigation className="w-4 h-4 text-accent" /> Google Maps
              </div>
            </div>
          </div>
          <div className="relative z-10 text-xs text-white/60 font-mono drop-shadow">v1.0 · Naples, FL</div>
        </div>

        {/* Right: form */}
        <div className="flex items-center justify-center p-8">
          <Card className="w-full max-w-md p-8 bg-card/90 border-border backdrop-blur-xl shadow-2xl shadow-black/60" data-testid="login-card">
          <div className="mb-6">
            <div className="text-xs font-mono text-primary tracking-widest mb-2">MI TAXI NAPLES · LOGIN</div>
            <h2 className="text-3xl font-black tracking-tight" style={{ fontFamily: "Manrope" }}>
              Iniciar sesión
            </h2>
            <p className="text-sm text-muted-foreground mt-2">
              Elige cómo vas a entrar.
            </p>
          </div>

          {processingGoogle && (
            <div className="mb-4 rounded-md border border-primary/40 bg-primary/10 p-3 text-xs text-primary font-mono">
              Verificando sesión de Google…
            </div>
          )}

          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-2 mb-6 bg-secondary/40" data-testid="login-tabs">
              <TabsTrigger value="dispatcher" data-testid="login-tab-dispatcher">
                <Headset className="w-3.5 h-3.5 mr-1.5" /> Dispatcher
              </TabsTrigger>
              <TabsTrigger value="driver" data-testid="login-tab-driver">
                <Car className="w-3.5 h-3.5 mr-1.5" /> Chofer
              </TabsTrigger>
            </TabsList>

            <TabsContent value="dispatcher" className="space-y-4 mt-0">
              <Button
                type="button"
                onClick={startGoogleLogin}
                variant="outline"
                className="w-full h-11 gap-3 bg-white text-black hover:bg-gray-100 border-transparent font-semibold"
                data-testid="google-login-btn"
              >
                <GoogleIcon /> Continuar con Google
              </Button>
              <div className="relative py-1">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-[10px] font-mono text-muted-foreground uppercase tracking-widest">
                  <span className="bg-card px-2">o con email</span>
                </div>
              </div>
              <LoginForm
                email={email} setEmail={setEmail}
                password={password} setPassword={setPassword}
                busy={busy} submit={submit}
                testIdPrefix="dispatcher"
              />
              <p className="text-[10px] text-muted-foreground text-center leading-relaxed">
                Solo cuentas de administrador o dispatcher registradas por el admin pueden entrar.
              </p>
            </TabsContent>

            <TabsContent value="driver" className="space-y-4 mt-0">
              <div className="text-xs text-muted-foreground bg-secondary/40 rounded-md p-3 border border-border">
                Ingresa con el usuario y contraseña que te dio el administrador.
              </div>
              <LoginForm
                email={email} setEmail={setEmail}
                password={password} setPassword={setPassword}
                busy={busy} submit={submit}
                testIdPrefix="driver"
              />
            </TabsContent>
          </Tabs>
        </Card>
      </div>
      </div>
    </div>
  );
}

function LoginForm({ email, setEmail, password, setPassword, busy, submit, testIdPrefix }) {
  return (
    <form onSubmit={submit} className="space-y-4" data-testid={`${testIdPrefix}-login-form`}>
      <div className="space-y-2">
        <Label htmlFor={`${testIdPrefix}-email`}>Email</Label>
        <Input
          id={`${testIdPrefix}-email`}
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid={`${testIdPrefix}-login-email-input`}
          className="bg-input border-border"
          placeholder="tu@correo.com"
          autoComplete="username"
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor={`${testIdPrefix}-password`}>Contraseña</Label>
        <Input
          id={`${testIdPrefix}-password`}
          type="password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          data-testid={`${testIdPrefix}-login-password-input`}
          className="bg-input border-border"
          placeholder="••••••••"
          autoComplete="current-password"
        />
      </div>
      <Button
        type="submit"
        disabled={busy}
        className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold h-11"
        data-testid={`${testIdPrefix}-login-submit-button`}
      >
        {busy ? "Entrando..." : "Entrar"}
      </Button>
    </form>
  );
}
